import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Bell, Menu, LogOut, User, Building2, Users } from "lucide-react";
import { hasPerm } from "@/lib/perms";
import { roleBlocksPath } from "@/lib/nav";
import { PawktLogo } from "@/components/pawkt/PawktLogo";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initials, inr } from "@/lib/format";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GlobalSearch } from "@/components/layout/GlobalSearch";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function Topbar({ onMenu }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchOpen, setSearchOpen] = useState(false);

  const { data: notif } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications?limit=15")).data,
    refetchInterval: 30000,
  });

  // Keyboard shortcuts: "/" (unless typing in a field) and Cmd/Ctrl+K open the palette.
  useEffect(() => {
    const h = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault(); setSearchOpen(true);
      } else if (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey) {
        e.preventDefault(); setSearchOpen(true);
      }
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, []);

  const markRead = async () => { await api.post("/notifications/read-all"); qc.invalidateQueries({ queryKey: ["notifications"] }); };

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-2 sm:gap-3 border-b border-pawkt-border bg-white/85 backdrop-blur-md px-3 sm:px-6">
      <button className="shrink-0 lg:hidden text-pawkt-text-secondary" onClick={onMenu} data-testid="menu-toggle" aria-label="Menu">
        <Menu className="h-5 w-5" />
      </button>

      <PawktLogo variant="compact" className="shrink-0 lg:hidden" />

      <div className="relative min-w-0 flex-1 max-w-md">
        <button
          type="button"
          data-testid="global-search-trigger"
          onClick={() => setSearchOpen(true)}
          className="flex w-full min-w-0 items-center gap-2 rounded-full border border-pawkt-border bg-pawkt-elevated pl-3 sm:pl-4 pr-3 py-2.5 text-sm text-pawkt-text-disabled transition-colors hover:border-primary/40 hover:bg-white">
          <Search className="h-4 w-4 shrink-0" />
          <span className="min-w-0 truncate">Search customers, pets, invoices…</span>
          <kbd className="ml-auto hidden sm:inline rounded border border-pawkt-border bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-400">⌘K</kbd>
        </button>
      </div>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

      <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-1.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="notifications-btn" className="relative rounded-full p-2 text-pawkt-text-secondary hover:bg-pawkt-elevated" aria-label="Notifications">
              <Bell className="h-5 w-5" />
              {notif?.unread > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">
                  {notif.unread}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[min(20rem,calc(100vw-1.5rem))] bg-white">
            <div className="flex items-center justify-between px-2 py-1.5">
              <DropdownMenuLabel className="px-0">Notifications</DropdownMenuLabel>
              <button onClick={markRead} className="text-xs text-emerald-600 hover:underline">Mark all read</button>
            </div>
            <DropdownMenuSeparator />
            <div className="max-h-80 overflow-auto">
              {(notif?.items || []).length === 0 && <div className="px-3 py-6 text-center text-sm text-slate-400">No notifications</div>}
              {(notif?.items || []).map((n) => (
                <div key={n.id} className="px-3 py-2 hover:bg-slate-50">
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${n.read ? "bg-slate-300" : "bg-emerald-500"}`} />
                    <p className="text-sm font-medium text-slate-800">{n.title}</p>
                  </div>
                  <p className="pl-4 text-xs text-slate-500">{n.message}</p>
                </div>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="hidden sm:flex items-center gap-2 rounded-full border border-pawkt-border bg-white px-3 py-1.5">
          <Building2 className="h-4 w-4 text-pawkt-text-disabled" />
          <span className="text-sm font-semibold text-pawkt-text max-w-[140px] truncate">{user?.tenant?.name}</span>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-btn" className="flex items-center gap-2 rounded-full p-1 hover:bg-pawkt-elevated">
              <div className="h-8 w-8 rounded-full pawkt-gradient-primary text-white flex items-center justify-center text-xs font-bold">
                {initials(user?.name)}
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-white">
            <DropdownMenuLabel>
              <div className="font-medium text-slate-800">{user?.name}</div>
              <div className="text-xs font-normal text-slate-400">{user?.email}</div>
              <div className="mt-1 text-xs font-normal capitalize text-emerald-600">{user?.role}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {!roleBlocksPath(user, "/settings") && (
            <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
              <User className="mr-2 h-4 w-4" /> Settings
            </DropdownMenuItem>
            )}
            {hasPerm(user, "settings.edit") && (
              <DropdownMenuItem onClick={() => navigate("/team")} data-testid="menu-staff-management">
                <Users className="mr-2 h-4 w-4" /> Staff Management
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-rose-600 focus:text-rose-600">
              <LogOut className="mr-2 h-4 w-4" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
